import Context from "shared/util/Context";
import OkC from "shared/util/OkcApi";
import isApp from "shared/util/isApp";
import _str from "shared/util/_str";
import Cookies from "shared/util/Cookies";
import Facebook from "shared/util/Facebook";
import ClientStats from "shared/util/ClientStats";
import AppPhoneCommand from "shared/util/AppPhoneCommand";
import getApolloClient from "shared/util/getApolloClient";
import {
	STATUS_MESSAGES,
	STATUS_CODES,
	SCREENS,
	CUSTOM_PREVIOUS_SCREENS,
	CUSTOM_ERRORS,
} from "apps/login/util/constants";
import { trackSignInSelectedFacebook, trackSigninWithEmailPasswordResponse } from "analytics/login";
import { getDeviceId } from "shared/util/deviceId";
import {
	LOG_IN_WITH_EMAIL,
	REENABLE_ACCOUNT,
	getEmailLoginResponse,
	getReenableLoginResponse,
	graphqlLoginPayloadToLegacy,
} from "apps/login/util/mutations";


// action types
// ----------------------------------------------------------------------------

export const LOADED = "LOGIN.LOADED";
export const SUBMIT_LOGIN = "LOGIN.SUBMIT_LOGIN";
export const SUBMIT_LOGIN_SUCCESS = "LOGIN.SUBMIT_LOGIN_SUCCESS";
export const SUBMIT_LOGIN_FAIL = "LOGIN.SUBMIT_LOGIN_FAIL";
export const SUBMIT_RESET = "LOGIN.SUBMIT_RESET";
export const SUBMIT_RESET_SUCCESS = "LOGIN.SUBMIT_RESET_SUCCESS";
export const SUBMIT_RESET_FAIL = "LOGIN.SUBMIT_RESET_FAIL";
export const CHANGE_SCREEN = "LOGIN.CHANGE_SCREEN";
export const SHOW_REACTIVATE = "LOGIN.SHOW_REACTIVATE";
export const SHOW_TWO_FACTOR = "LOGIN.SHOW_TWO_FACTOR";
export const SHOW_SMS_2_FACTOR = "LOGIN.SHOW_SMS_2_FACTOR";
export const SHOW_CAPTCHA = "LOGIN.SHOW_CAPTCHA";
export const CONFIRM_RECAPTCHA_SUCCESS = "LOGIN.CONFIRM_RECAPTCHA_SUCCESS";
export const SAVE_REACTIVATE_PARAMS = "LOGIN.SAVE_REACTIVATE_PARAMS";


// initial state
// ----------------------------------------------------------------------------

export const INITIAL_STATE = {
	reactivateAuthcode: null,
	reactivateUserid: null,
	screen: null,
	previousScreen: SCREENS.LOGIN,
	isLoading: false,
	isLoggedIn: false,
	isSubmitting: false,
	showFacebookLogin: true,
	showTwoFactor: false,
	error: null,
	userid: null,
};

const getInitialState = () => {
	let initialState = {
		...INITIAL_STATE,
	};

	if (typeof window !== "undefined" && window.LoginParams) {
		initialState = {
			...initialState,
			...window.LoginParams,
		};
	}

	return initialState;
};


// reducer
// ----------------------------------------------------------------------------

export function reducer(state = getInitialState(), action) {
	switch (action.type) {
		case SUBMIT_LOGIN:
			return {
				...state,
				isLoading: true,
				isSubmitting: true,
			};

		case SUBMIT_LOGIN_SUCCESS:
			return {
				...state,
				isLoading: false,
				isSubmitting: false,
				isLoggedIn: true,
			};

		case SUBMIT_LOGIN_FAIL:
			return {
				...state,
				isLoading: false,
				isSubmitting: false,
				error: action.error,
			};

		case SUBMIT_RESET:
			return {
				...state,
				isLoading: true,
			};

		case SUBMIT_RESET_SUCCESS:
			return {
				...state,
				isLoading: false,
				screen: SCREENS.PASSWORD_SUBMIT,
			};

		case SUBMIT_RESET_FAIL:
			return {
				...state,
				isLoading: false,
				error: action.error,
			};

		case CHANGE_SCREEN: {
			const { screen, previousScreen } = action;
			return {
				...state,
				screen,
				previousScreen: previousScreen || CUSTOM_PREVIOUS_SCREENS[screen] || state.screen,
			};
		}
		case SHOW_REACTIVATE:
			return {
				...state,
				reactivateAuthcode: action.authcode,
				reactivateUserid: action.userid,
				screen: SCREENS.REACTIVATE,
			};

		case SHOW_TWO_FACTOR:
			return {
				...state,
				showTwoFactor: true,
				isSubmitting: false,
			};

		case SHOW_SMS_2_FACTOR:
			return {
				...state,
				isSubmitting: false,
				screen: SCREENS.SMS_TWO_FACTOR,
				previousScreen: SCREENS.LOGIN,
				userid: action.payload.userid,
			};

		case SHOW_CAPTCHA:
			return {
				...state,
				isSubmitting: false,
				screen: SCREENS.CAPTCHA,
			};

		case SAVE_REACTIVATE_PARAMS:
			return {
				...state,
				reactivateAuthcode: action.payload.authCode,
				reactivateUserid: action.payload.userid,
			};

		default:
			return state;
	}
}


// action creators
// ----------------------------------------------------------------------------

export const submitLoginError = (error) => ({
	type: SUBMIT_LOGIN_FAIL,
	error: {
		type: error.type || "Login Error",
		message: _str(error.message || "Login Error"),
	},
});

const submitResetFail = (error) => ({
	type: SUBMIT_RESET_FAIL,
	error,
});

export function _goHome({ onboardingIncomplete } = {}) {
	if (isApp()) {
		try {
			AppPhoneCommand("cancelAllScheduledNotifications");
			AppPhoneCommand("registerForRemoteNotifications");
			window.appStorage.setItem("isUser", true);
		} catch (e) {
			// do nothing and hope I guess?
		}

		// Yo, this needs to stay "quickmatch" because of how the apps determine
		// what views are native! Don't be all like "oh we named this fucker to
		// double whatever I'll change it here" 'cause then Jordan will come and
		// break yer kneecaps and nobody wants that right?
		let landingPage = "/quickmatch";

		if (onboardingIncomplete) {
			landingPage = "/onboarding";
		} else if (window.location.pathname.indexOf("/l/") === 0) {
			landingPage = window.location.href;
		}

		window.App.loadPage(landingPage, {
			post_login: 1,
			clear_history: 1,
		});
	} else if (onboardingIncomplete) {
		window.location.href = "/onboarding";
	} else if (window.location.pathname.indexOf("/l/") === 0) {
		window.location.reload();
	} else if (process.env.PLATFORM === "desktop") {
		window.location.href = "/home";
	} else {
		window.location.href = "/doubletake";
	}
}

export function loadLogin() {
	/**
	  * This sets a cookie on the browser via javascript. This means if we see
	  * a login that doesn't contain this cookie chances are the login was
	  * automated. This is negated by tools that instrument a real browser or
	  * people who catch onto the cookie. However, this still addresses the
	  * low hanging fruit of automated logins.
	  */
	Cookies.setCookie("ua", "531227642bc86f3b5fd7103a0c0b4fd6");

	// load the FB lib
	Facebook.load();
	return { type: LOADED };
}

/**
 * This helper is a generic handler for login error catching; it works for both
 * OkC login and facebook login, and dispatches new actions accordingly.
 *
 * @param {dict} res - the result of the api call
 * @param {func} dispatch - a redux dispatcher
 */
const _handleLoginError = (res, dispatch) => {
	let error = null;

	if (res) {
		const type = res.status;

		if (type === STATUS_CODES.DISABLED) {
			dispatch({
				type: SHOW_REACTIVATE,
				authcode: res.reenable_authcode,
				userid: res.reenable_userid,
			});
			return;
		} else if (type === STATUS_CODES.NEEDS_2FA) {
			dispatch({ type: SHOW_TWO_FACTOR });
			return;
		} else if (type === STATUS_CODES.NEED_CAPTCHA) {
			dispatch({ type: SHOW_CAPTCHA });
			return;
		} else if (type === STATUS_CODES.NEED_SMS_2FA) {
			dispatch({
				type: SHOW_SMS_2_FACTOR,
				payload: { userid: res.userid },
			});
			return;
		}

		if (STATUS_MESSAGES[type]) {
			error = {
				type,
				message: STATUS_MESSAGES[type],
			};
		} else if (res.error) {
			error = res.error;
		}
	}

	if (!error) {
		error = {
			type: "UNKNOWN",
			message: _str("Something went wrong, please try again later"),
		};
	}

	// shake the phone bro
	AppPhoneCommand("vibratePhone");

	dispatch(submitLoginError(error));
};

/**
 * Handle the account reactivation flow.
 * @param {Object} params - legacy-formatted reactivation params.
 * @param {string} params.reenable_userid - reactivation userid.
 * @param {string} params.reenable_authcode - reactivation authcode to bypass password.
 * @param {Object} options - an object with options.
 * @return {Function} - a redux thunk.
 */
const _reactivate = (params, { notifyIOS } = {}) => {
	return (dispatch) => {
		const apolloClient = getApolloClient();
		const mutation = REENABLE_ACCOUNT;
		const variables = {
			input: {
				userid: params.reenable_userid,
				authCode: params.reenable_authcode,
			},
		};
		const options = { mutation, variables };

		apolloClient.mutate(options)
			.then((_res) => {
				// Translate the new thing to the old thing.
				const graphqlRes = getReenableLoginResponse(_res) || {};
				const res = graphqlLoginPayloadToLegacy(graphqlRes);

				if (!res || res.status) {
					_handleLoginError(res, dispatch);
					return;
				}

				ClientStats.update("login - onboarding funnel - login - success");
				dispatch({ type: SUBMIT_LOGIN_SUCCESS });

				if (notifyIOS) {
					AppPhoneCommand("smsAccountReactivationComplete");
				}

				_goHome({ onboardingIncomplete: res.onboarding_incomplete });
			})
			.catch((err) => {
				ClientStats.update("login - onboarding funnel - login - failed");
				_handleLoginError(err, dispatch);
			});
	};
};

export function reactivateAccount(options) {
	return (dispatch, getStore) => {
		dispatch({ type: SUBMIT_LOGIN });

		const state = getStore().login;

		// We need both of these
		if (!state.reactivateUserid || !state.reactivateAuthcode) {
			return;
		}

		const params = {
			reenable_userid: state.reactivateUserid,
			reenable_authcode: state.reactivateAuthcode,
		};

		dispatch(_reactivate(params, options));
	};
}

/**
 * Handle the login flow for username/email + password.
 * @param {Object} params - legacy-formatted login params.
 * @param {string} params.username - an email address or OkCupid username.
 * @param {string} params.password - the account password associated with the username parameter.
 * @param {string} params.deviceid - unique device id.
 * @param {boolean} params.captcha - whether a captcha was forced.
 * @param {string} params.recaptcha_response - a captcha code.
 * @param {string} params.totp_code - a temporary one time password code (staff login).
 * @param {boolean} params.totp_save_device - whether to remember this device (staff login).
 * @return {Function} - a redux thunk.
 */
const _login = (params = {}) => {
	return (dispatch) => {
		const apolloClient = getApolloClient();
		const mutation = LOG_IN_WITH_EMAIL;
		const variables = {
			input: {
				email: params.username,
				password: params.password,
				deviceId: params.deviceid,
				forceCaptcha: !!params.captcha,
				recaptchaResponse: params.recaptcha_response,
				authCode: params.totp_code,
				saveAuth: params.totp_code
					? !!params.totp_save_device
					: undefined,
			},
		};
		const options = { mutation, variables };

		apolloClient.mutate(options)
			.then((_res) => {
				// Translate the new thing to the old thing.
				const graphqlRes = getEmailLoginResponse(_res) || {};
				const res = graphqlLoginPayloadToLegacy(graphqlRes);

				if (!res || res.status) {
					trackSigninWithEmailPasswordResponse(res.status_str || "login failure");
					_handleLoginError(res, dispatch);
					return;
				}

				trackSigninWithEmailPasswordResponse("success");
				ClientStats.update("login - onboarding funnel - login - success");
				dispatch({ type: SUBMIT_LOGIN_SUCCESS });

				_goHome({ onboardingIncomplete: res.onboarding_incomplete });
			})
			.catch((err) => {
				trackSigninWithEmailPasswordResponse("login failure");
				ClientStats.update("login - onboarding funnel - login - failed");
				_handleLoginError(err, dispatch);
			});
	};
};

export function submitLogin(args) {
	return (dispatch) => {
		dispatch({ type: SUBMIT_LOGIN });
		const missingCredentials = !args.username || !args.password;

		if (missingCredentials) {
			dispatch(submitLoginError({
				type: "Incomplete Form",
				message: _str(`Uh-oh! Please enter your ${!args.username ? "email" : "password"} to login.`),
			}));

			return;
		}

		const handleLogin = (deviceid) => {
			const params = {
				username: args.username,
				password: args.password,
				deviceid,
			};

			if (Context.get("state").FORCE_CAPTCHA_LOGIN) {
				params.captcha = 1;
			}

			if (args.authcode) {
				params.totp_code = args.authcode;
				params.totp_save_device = args.saveAuth;
			}

			if (args.captchaKey) {
				params.recaptcha_response = args.captchaKey;
			}

			dispatch(_login(params));
		};

		getDeviceId()
			.then((deviceid) => handleLogin(deviceid))
			.catch(() => handleLogin(""));
	};
}

export function facebookLogin() {
	return (dispatch) => {
		dispatch({ type: SUBMIT_LOGIN });

		ClientStats.add("login - onboarding funnel - facebook - clicked");
		trackSignInSelectedFacebook();

		Facebook.login()
			.then((res) => {
				if (!res || res.status) {
					_handleLoginError(res, dispatch);
					return;
				}

				ClientStats.update({
					statname: "login - onboarding funnel - facebook - success",

					cb() {
						dispatch({ type: SUBMIT_LOGIN_SUCCESS });
						_goHome();
					},
				});
			})
			.catch((facebookError) => {
				let error = {
					error: {
						type: CUSTOM_ERRORS.FACEBOOK_EXITED,
						message: "Facebook Login Failed",
					},
				};

				// if the user clicks x on the facebook popo there is no status_str
				// and we do not want to show an error message when this happens
				// login/app.jsx checks for CUSTOM_ERRORS.FACEBOOK_EXITED
				if (facebookError && facebookError.status_str) {
					error = facebookError;
				}

				ClientStats.update("login - onboarding funnel - facebook - failed");
				_handleLoginError(error, dispatch);
			});
	};
}

export function submitReset(email, successCallback) {
	return (dispatch) => {
		dispatch({
			type: SUBMIT_RESET,
			email,
		});

		OkC.post("/okc/lost_password", {
			data: { email },
		})
			.then((res) => {
				if (res.code) {
					dispatch(submitResetFail({
						type: res.message,
						message: res.display,
					}));
				} else {
					dispatch({ type: SUBMIT_RESET_SUCCESS });
				}

				if (typeof successCallback === "function") {
					successCallback();
				}
			})
			.catch(() => {
				dispatch(submitResetFail({
					type: "UNKNOWN",
					message: "Something went wrong, refresh and try again",
				}));
			});
	};
}

export const changeScreen = (screen, previousScreen) => ({
	type: CHANGE_SCREEN,
	screen,
	previousScreen,
});

export const saveReactivateParams = (userid, authCode) => ({
	type: SAVE_REACTIVATE_PARAMS,
	payload: {
		userid,
		authCode,
	},
});



// WEBPACK FOOTER //
// ./src/ducks/login.js